import { GrowthbookClientProvider } from "@openfeature/growthbook-client-provider";
import {
  FirstSuccessfulStrategy,
  WebMultiProvider,
} from "@openfeature/multi-provider-web";
import { ProviderEntryInput } from "@openfeature/multi-provider-web/src/lib/types";
import {
  EvaluationContext,
  OpenFeature,
  OpenFeatureProvider,
  useFlag,
} from "@openfeature/react-sdk";
import React, { ReactNode } from "react";
import {
  type LoaderFunction,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";

import { Analytics } from "modules/analytics";

/**
 * Feature flags and their "default" values that are currently available/tracked
 * in the admin web app
 */
const featureFlags = {
  /**
   * Assume that this flag is always "on" in dev environments, and always "off"
   * in production
   */
  isDev: true,
  "site-wide-promotions": false,
  "cancel-memberships": false,
  timeDelayedTreatmentGroups: false,
  "incomplete-profile-redirect-v-2": false,
  unitedDerm: false,
  patientSupportChat: false,
  "repeatmd-glp": false,
  membershipBeautyBank: true,
  guestAccess: false,
  /**
   * Controls the behavior of patients selecting their booking location
   * at checkout for multi location clients
   */
  selectPatientLocation: false,
  BuyXGetY: true,
  "cta-button-roundness": "4px",

  adonisHomeCTAText: {
    header: "Find the Right Treatment with {Name}",
    subheader: "{Pronoun}'s the answer to all your questions.",
    chat_box: "What are your goals?",
    chat_submit: "Ask {Name}",
  },

  adonisV3CTAText: {
    header: "Hey {username}, want to discover your next favorite treatment?",
    subheader: "",
    chat_box: "Start typing to chat",
    chat_submit: "Ask {Name}",
    link_text: "View all recommendations",
  },

  adonisRecommandationCarouselText: {
    header: "Recommended for {username}...",
    link: "View your personalized recommendations",
    chat_box: "Start typing to chat",
    chat_submit: "Ask {Name}",
  },

  adonisShopCTAText: {
    chat_box: "Not sure where to start?",
    chat_submit: "Ask {Name}",
  },

  adonisShopCategoryLinkText: {
    title: "Just for {username}",
    subtitle: "Recommended for you by {Name}",
    badge_text: "NEW PICKS",
  },

  adonisShopCategoryHeaderText: {
    title: "Just for you, {username}",
    subtitle: "Curated for you by {Name} AI",
  },

  adonisChatText: {
    header: "I'm {Name}, your AI treatment advisor.",
    new_chat: "NEW CHAT STARTED",
    chose_question_or_chat: "CHOOSE A QUESTION OR ASK BELOW TO START",
    chat_box: "Type a message...",
  },

  /*
   * Controls whether to show the "Flexible Membership Terms" feature
   */
  flexible_membership_terms: false,
  /**
   * Enable the referral program
   */
  "referral-rewards-phase-2": false,

  /**
   * Controls whether to show membership reward selection after checkout
   */
  selectMembershipRewardsAfterCheckout: true,

  /**
   * Dynamic text for Adonis CTA banner
   */
  adonisDynamicCTAText: {
    line1: "Find the Right",
    line2: "Treatment with {Name}",
  },
  // Controls whether to show the "Selling Products" feature
  "selling-products": true,
  beautyBankTerm: "Beauty Bank",
  /**
   * Controls whether to show the "Gift Cards" feature
   */
  giftcards: true,
  enableRepeatCashAddition: false,
  /**
   * Controls whether Algolia is used for search, filtering & sorting in the patient app
   */
  algoliaFiltering: false,
  hapticFeedback: true,
  /**
   * Controls whether to show the new v3 design system
   */
  v3: true,
  /**
   * Controls the Intercom app ID for web chat support
   */
  intercomAppId: "uas05fx3",
};

// Mapped type makes the IDE annotations a bit nicer
type Flags = { [K in keyof typeof featureFlags]: (typeof featureFlags)[K] };

// Override default values from environment variable for local development
import.meta.env.VITE_ENABLED_FEATURE_FLAGS?.split(",").forEach((flag) => {
  if (typeof featureFlags[flag as keyof Flags] === "boolean") {
    (featureFlags[flag as keyof Flags] as unknown) = true;
  } else {
    console.warn(
      `Unknown feature flag '${flag}' specified in VITE_ENABLED_FEATURE_FLAGS`
    );
  }
});

const providers: ProviderEntryInput[] = [];

if (import.meta.env.VITE_GROWTHBOOK_CLIENT_ID && !import.meta.env.VITEST) {
  providers.push({
    name: "growthbook",
    provider: new GrowthbookClientProvider(
      {
        clientKey: import.meta.env.VITE_GROWTHBOOK_CLIENT_ID,
        enableDevMode: import.meta.env.DEV,
        trackingCallback: (experiment, result) => {
          Analytics.track("Experiment Viewed", {
            experimentId: experiment.key,
            variationId: result.key,
          });
        },
      },
      {
        streaming: true,
        timeout: 2000, // important that this is less than the `clientReadyPromise` timeout
      }
    ),
  });
} else {
  console.warn(
    "VITE_GROWTHBOOK_CLIENT_ID variable not provided, feature flags will revert to default values"
  );
}

const clientReady = OpenFeature.setProviderAndWait(
  new WebMultiProvider(providers, new FirstSuccessfulStrategy())
);
const client = OpenFeature.getClient();

export const FFlagProvider = (props: { children: ReactNode }) => {
  return (
    <OpenFeatureProvider client={client}>{props.children}</OpenFeatureProvider>
  );
};

/**
 * Lower-level hooks API to subscribe to the value of a particular feature flag.
 */
export const useFeatureFlag = <Flag extends keyof Flags>(key: Flag) => {
  const { value } = useFlag<Flags[Flag]>(key, featureFlags[key]);
  return value;
};

/**
 * Lower-level API to get the current value of a particular feature flag.
 */
export const getFeatureFlag = async <Flag extends keyof Flags>(key: Flag) => {
  await clientReady;
  switch (typeof featureFlags[key]) {
    case "boolean":
      return client.getBooleanValue(
        key,
        featureFlags[key]
      ) as unknown as Flags[Flag];
    case "number":
      return client.getNumberValue(
        key,
        featureFlags[key]
      ) as unknown as Flags[Flag];
    case "string":
      return client.getStringValue(
        key,
        featureFlags[key]
      ) as unknown as Flags[Flag];
    default:
      return client.getObjectValue(key, featureFlags[key]) as Flags[Flag];
  }
};

/**
 * Wrapper component that will render its children only if a feature flag
 * matches the specified value
 *
 * If the `equals` prop is not specified, the children will be rendered if the
 * flag is truthy. Otherwise, they will be rendered only if the flag matches the
 * specified value.
 *
 * @example
 *
 * const MyComponent = () => {
 *   return (
 *     <>
 *       <IfFlag flag="isDev">
 *         <div>This will only be rendered in dev environments</div>
 *       </IfFlag>
 *
 *       <IfFlag flag="isDev" equals={false}>
 *         <div>This will only be rendered in production environments</div>
 *       </IfFlag>
 *    </>
 *   );
 * };
 */
export const IfFlag = <Flag extends keyof Flags>(props: {
  flag: Flag;
  equals?: Flags[Flag];
  children: React.ReactNode;
}) => {
  const flagValue = useFeatureFlag(props.flag);
  if (props.equals === undefined ? flagValue : flagValue === props.equals)
    return <>{props.children}</>;
  else return null;
};

/**
 * Wraps a route loader function to prevent loading the page based on the value
 * of a feature flag.
 *
 * If `equals` argument is not specified, the page will load if the flag is
 * truthy. Otherwise, it will load only if the flag matches the specified value.
 *
 * @example
 *
 * export const loader = loadIfFlag("isDev")(() => {
 *  return { message: "This page will only be reachable in dev environments" };
 * });
 */
export const loadIfFlag =
  <Flag extends keyof typeof featureFlags>(
    flag: Flag,
    equals?: (typeof featureFlags)[Flag]
  ) =>
  <Loader extends LoaderFunction>(fn: Loader) => {
    return async (args: LoaderFunctionArgs) => {
      const flagValue = await getFeatureFlag(flag);

      if (equals === undefined ? flagValue : flagValue === equals) {
        return fn(args) as ReturnType<Loader>;
      } else {
        throw redirect("/");
      }
    };
  };

/**
 * Returns the passed object conditionally based on the value of a feature flag.
 *
 * If `equals` argument is not specified, the object will be returned if the
 * flag is truthy. Otherwise, it will be returned only if it matches the
 * specified value.
 *
 * Don't use this in React components, use `<IfFlag />` or `useFeatureFlag()`
 * instead.
 *
 * @example
 *
 * const loader = () => {
 *   const environmentData = await ifFlag("isDev")
 *     ? getForDevelopment()
 *     : getForProduction();
 *
 *   return json(environmentData);
 * };
 */
export const ifFlag =
  <Flag extends keyof typeof featureFlags>(
    flag: Flag,
    equals?: (typeof featureFlags)[Flag]
  ) =>
  async <T,>(obj: T): Promise<T | null> => {
    const flagValue = await getFeatureFlag(flag);
    if (equals === undefined ? flagValue : flagValue === equals) return obj;
    else return null;
  };

type UserContext = {
  id: string;
  name?: string;
  email?: string;
};

type PlatformContext = {
  id: string;
  domain?: string;
  created: Date;
};

/**
 * Identify the current user context for feature flagging
 */
export const identifyFlagUser = (
  user: UserContext,
  platform?: PlatformContext | null
) => {
  return OpenFeature.setContext({
    user: {
      targetingKey: user.id,
      name: user.name ?? null,
      email: user.email ?? null,
    },
    platform: platform
      ? {
          targetingKey: platform.id,
          domain: platform.domain ?? null,
          created: platform.created,
        }
      : null,
  });
};
